import { ProductosModel } from '../../../../../models/productos/productos.model';
import { DropdownData } from '@fitogest/reactive-form-builder/interfaces';

export type ProductosFitosanitariosProductPatch = Pick<ProductosModel, 'nombre' | 'materia_activa'>;

export interface ProductosFitosanitariosEditionSelection {
    product: ProductosModel | null;
    useOtherCommonDenomination: boolean;
}

export interface ProductosFitosanitariosEditionSelectionParams {
    model: ProductosModel;
    standardProducts: ProductosModel[];
    otherDenominationProducts: ProductosModel[];
}

export interface ProductosFitosanitariosSearchTermParams {
    currentName: string;
    selectedOtherDenomination: ProductosModel | null;
    useOtherCommonDenomination: boolean;
}

export interface ProductosFitosanitariosDropdownSourceParams {
    standardProducts: DropdownData<ProductosModel>;
    otherDenominationProducts: DropdownData<ProductosModel>;
    useOtherCommonDenomination: boolean;
}

export interface ProductosFitosanitariosDropdownSource {
    dropdownData: DropdownData<ProductosModel>;
    valuePrimaryKey: 'num_registro' | 'referencia';
}

export function resolveProductosFitosanitariosDropdownSource(
    params: ProductosFitosanitariosDropdownSourceParams,
): ProductosFitosanitariosDropdownSource {
    if (params.useOtherCommonDenomination) {
        return {
            dropdownData: params.otherDenominationProducts,
            valuePrimaryKey: 'num_registro',
        };
    }

    return {
        dropdownData: params.standardProducts,
        valuePrimaryKey: 'referencia',
    };
}

export function buildProductosFitosanitariosSelectionResetPatch(): Pick<ProductosModel, 'num_registro' | 'nombre' | 'materia_activa'> {
    return {
        num_registro: null,
        nombre: '',
        materia_activa: '',
    };
}

export function isProductosFitosanitariosOtherDenominationEnabled(value: unknown): boolean {
    return value === '1' || value === 1 || value === true;
}

export function buildProductosFitosanitariosProductPatch(
    product: ProductosModel,
    useOtherCommonDenomination: boolean,
): ProductosFitosanitariosProductPatch {
    const productName = useOtherCommonDenomination
        ? firstNonEmptyProductString(product.denominaciones_comunes, product.nombre_comercial, product.nombre)
        : firstNonEmptyProductString(product.nombre_comercial, product.nombre);

    return {
        nombre: productName,
        materia_activa: getProductosFitosanitariosProductActiveMatter(product),
    };
}

export function getProductosFitosanitariosProductRegisterValue(product: ProductosModel): string | number {
    const reference = toScalarProductIdentifier(product.referencia);

    if (reference !== '') {
        return reference;
    }

    return toScalarProductIdentifier(product.num_registro);
}

export function getProductosFitosanitariosProductSearchTerm(product: ProductosModel): string {
    return firstNonEmptyProductString(
        product.referencia,
        product.num_registro,
        product.nombre_comercial,
        product.nombre,
    );
}

export function getProductosFitosanitariosVademecumSearchTerm(params: ProductosFitosanitariosSearchTermParams): string {
    if (!params.useOtherCommonDenomination || params.selectedOtherDenomination === null) {
        return params.currentName;
    }

    return firstNonEmptyProductString(params.selectedOtherDenomination.nombre_comercial, params.currentName);
}

export function resolveProductosFitosanitariosEditionSelection(
    params: ProductosFitosanitariosEditionSelectionParams,
): ProductosFitosanitariosEditionSelection {
    const explicitOtherCommonDenomination = isProductosFitosanitariosOtherDenominationEnabled(
        params.model.otra_denominacion_comun,
    );
    const otherProduct = findProductosFitosanitariosOtherDenominationProduct(params.model, params.otherDenominationProducts, false);

    if (explicitOtherCommonDenomination) {
        return {
            product: otherProduct,
            useOtherCommonDenomination: true,
        };
    }

    const inferredOtherProduct = findProductosFitosanitariosOtherDenominationProduct(
        params.model,
        params.otherDenominationProducts,
        true,
    );

    if (inferredOtherProduct !== null) {
        return {
            product: inferredOtherProduct,
            useOtherCommonDenomination: true,
        };
    }

    return {
        product: findProductosFitosanitariosStandardProduct(params.model, params.standardProducts),
        useOtherCommonDenomination: false,
    };
}

export function matchesProductosFitosanitariosProductIdentifier(value: unknown, selectedValue: unknown): value is ProductosModel {
    if (!isProductosFitosanitariosProductModel(value)) {
        return false;
    }

    const selected = String(selectedValue ?? '').trim();

    if (selected.length === 0) {
        return false;
    }

    return [value.referencia, value.num_registro]
        .map((identifier) => String(identifier ?? '').trim())
        .includes(selected);
}

export function isProductosFitosanitariosProductModel(value: unknown): value is ProductosModel {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeProductosFitosanitariosComparableString(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
}

function findProductosFitosanitariosStandardProduct(model: ProductosModel, products: ProductosModel[]): ProductosModel | null {
    return products.find((product) => hasSameProductIdentifier(product.referencia, model.num_registro)) ?? null;
}

function findProductosFitosanitariosOtherDenominationProduct(
    model: ProductosModel,
    products: ProductosModel[],
    requireNameMatch: boolean,
): ProductosModel | null {
    return products.find((product) => {
        if (!hasSameProductIdentifier(product.num_registro, model.num_registro)) {
            return false;
        }

        return !requireNameMatch || matchesEditionProductName(product, model);
    }) ?? null;
}

function matchesEditionProductName(product: ProductosModel, model: ProductosModel): boolean {
    const modelName = normalizeProductosFitosanitariosComparableString(model.nombre);

    if (modelName.length === 0) {
        return false;
    }

    return [
        product.denominaciones_comunes,
        product.nombre_comercial,
        product.nombre,
    ]
        .map((value) => normalizeProductosFitosanitariosComparableString(value))
        .includes(modelName);
}

function hasSameProductIdentifier(left: unknown, right: unknown): boolean {
    const normalizedLeft = normalizeProductosFitosanitariosComparableString(left);
    const normalizedRight = normalizeProductosFitosanitariosComparableString(right);

    return normalizedLeft.length > 0 && normalizedLeft === normalizedRight;
}

function getProductosFitosanitariosProductActiveMatter(product: ProductosModel): string {
    const nestedProduct = product.producto as Partial<ProductosModel> | undefined;

    return firstNonEmptyProductString(
        product.composicion,
        product.materia_activa,
        nestedProduct?.composicion,
        nestedProduct?.materia_activa,
    );
}

function firstNonEmptyProductString(...values: unknown[]): string {
    return values
        .map((value) => String(value ?? '').trim())
        .find((value) => value.length > 0 && value !== '-') ?? '';
}

function toScalarProductIdentifier(value: unknown): string | number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        return value.trim();
    }

    return '';
}
